/**
 * `/fly`'s always-on recorder.
 *
 * The original asked before the flight — "Each time you enter the simulation,
 * Red Baron will ask you if you wish to record the mission" — because a 1990
 * machine had to know in advance whether it could afford the disk traffic. It
 * then asked again after: "Would you like to save this mission to a tape?"
 * (verbatim, `RB.EXE`).
 *
 * This port drops the first question and keeps the second. Recording costs
 * about three microseconds a tick and a few kilobytes a sortie
 * (docs/engineering/replay.md), which is far below the threshold at which
 * asking would be doing the pilot a favour — and the flight you most want a
 * tape of is always the one you did not expect to be worth keeping.
 *
 * The session is a thin thing on purpose: it decides *what is being recorded*
 * (which subject, whose stick, what the tape is called) and hands the actual
 * collecting to the engine's `ReplayRecorder`. Everything about the format
 * lives there.
 */

import {
  ReplayRecorder,
  tapeName,
  type EncounterSetup,
  type EncounterState,
  type FlightInputs,
  type MissionSpec,
  type ReplayTape,
  type TapeSubject,
} from "@red-opus/engine";

export interface RecordingSession {
  /** Wire straight to `FlightSceneCallbacks.onFlown`. */
  readonly onFlown: (inputs: FlightInputs, ticks: number, encounter: EncounterState) => void;
  /** Ticks captured so far — zero until the flight actually starts. */
  readonly ticks: () => number;
  /** The name to offer in the save prompt, derived from what was flown. */
  readonly suggestedName: () => string;
  readonly seal: (name: string) => ReplayTape;
}

/**
 * A tape name from a mission title: the original's own shape, since its shipped
 * tapes were `SPLIT_S`, `HALFLOOP`, `IMMEL`. Words are joined rather than
 * spaced because a DOS filename had no room for a space, and the eight-character
 * trim happens in the engine (`tapeName`).
 *
 * The trailing separator is dropped after the trim, not before it: "Balloon
 * busting" cuts to `BALLOON_`, and a name ending in an underscore reads like it
 * was cut off — which it was, but saying so with a stray character is worse
 * than simply not saying it.
 */
export function suggestNameFor(title: string): string {
  return tapeName(tapeName(title.replace(/[^A-Za-z0-9]+/g, "_")).replace(/_+$/, ""));
}

export function createRecordingSession(subject: TapeSubject, playerId: string, title: string): RecordingSession {
  const recorder = new ReplayRecorder({ subject, playerId, title });
  return {
    onFlown: (inputs, ticks, encounter) => {
      recorder.flew(inputs, ticks);
      recorder.observe(encounter);
    },
    ticks: () => recorder.ticks,
    suggestedName: () => suggestNameFor(title),
    seal: (name) => recorder.seal(name, new Date().toISOString()),
  };
}

/**
 * What `/fly` is recording. A mission carries objectives and a debrief; the
 * instant dogfight is a bare encounter, which has neither and is still worth a
 * tape — the original's own `BAR_ROLL.VCR` and `LOOP.VCR` are recordings of
 * nothing but flying.
 */
export function subjectFor(mission: MissionSpec | undefined, encounter: EncounterSetup): TapeSubject {
  return mission ? { kind: "mission", mission } : { kind: "encounter", encounter };
}
